import { useCallback, useEffect, useState } from 'react';
import { displayName, type UserProfile } from '@visionqc/shared';
import type { Customer } from '@visionqc/shared';
import { api } from '../api/client';
import { Combobox } from '../components/Combobox';
import { UserDetailView } from './UserDetail';
import type { CustomerDetail } from './CustomerDetail';
import { useAuth, useIsAdmin } from '../api/auth';
import { fmtTime } from '../lib/format';

/**
 * User administration.
 *
 * The form is long, so it is grouped the way someone filling it in thinks:
 * who they are, how to reach them, who they work for. Only the login name is
 * required — an operator should never be kept off the system because nobody
 * knows their postcode.
 */

type Draft = Partial<UserProfile> & { password?: string; pin?: string };

const EMPTY: Draft = { role: 'OPERATOR', active: true, country: 'France' };

export function UsersView() {
  const isAdmin = useIsAdmin();
  const session = useAuth().user;
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [openUser, setOpenUser] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers(await api.listUsers());
      setError(null);
    } catch (e) {
      // Surface the failure instead of showing an empty list as if there were
      // no users (the same fix already applied to the customer picker below).
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAdmin) {
    return (
      <div>
        <h1 className="view-title">Users</h1>
        <div className="panel muted">Managing accounts requires the ADMIN role.</div>
      </div>
    );
  }

  // A detail page rather than a route, matching the fleet list: the list is
  // the way in, and a deep link to a deleted account would only 404.
  if (openUser !== null && !editing) {
    return (
      <UserDetailView
        id={openUser}
        onBack={() => {
          setOpenUser(null);
          void load();
        }}
        onEdit={(u) => setEditing(u)}
      />
    );
  }

  const remove = async (u: UserProfile) => {
    if (
      !window.confirm(
        `Delete ${displayName(u)}?\n\nTheir annotations keep their name, so the inspection ` +
          `history stays readable. This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await api.deleteUser(u.id);
      setNotice(`${displayName(u)} deleted.`);
      setError(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Users</h1>
        <button className="btn primary" onClick={() => setEditing(editing ? null : { ...EMPTY })}>
          {editing ? '✕ Cancel' : '+ New user'}
        </button>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      {editing && (
        <UserForm
          draft={editing}
          onCancel={() => setEditing(null)}
          onSaved={async (msg) => {
            setEditing(null);
            setNotice(msg);
            setError(null);
            await load();
          }}
          onError={setError}
        />
      )}

      <div className="panel">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th>Role</th>
              <th>Sign-in</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={{ opacity: u.active ? 1 : 0.5 }}>
                <td>
                  <button
                    style={{
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      color: 'var(--accent)',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 'inherit',
                    }}
                    onClick={() => setOpenUser(u.id)}
                  >
                    {displayName(u)}
                  </button>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {u.name}
                    {!u.active && ' · deactivated'}
                    {u.id === session?.id && ' · you'}
                  </div>
                </td>
                <td className="muted">{u.email ?? '—'}</td>
                {/* For a tenant login the real "company" is the linked customer,
                    not the free-text field — showing that stops the two from
                    looking like a mismatch. Staff keep their own company. */}
                <td className="muted">
                  {u.role === 'CUSTOMER' ? (u.customerName ?? '—') : (u.companyName ?? '—')}
                </td>
                <td>
                  <span className={`badge ${u.role === 'ADMIN' ? 'unsure' : ''}`}>{u.role}</span>
                </td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {/* What credentials exist, never anything derived from them. */}
                  {u.hasPassword ? 'password' : '—'}
                  {u.hasPin && ' + PIN'}
                  {u.lockedUntil && new Date(u.lockedUntil) > new Date() && (
                    <span style={{ color: 'var(--warn)' }}> · locked</span>
                  )}
                </td>
                <td className="muted" style={{ fontSize: 12 }}>{fmtTime(u.updatedAt)}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn sm" onClick={() => setOpenUser(u.id)} title="Open">
                    →
                  </button>
                  <button className="btn sm" onClick={() => setEditing(u)} title="Edit">
                    ✎
                  </button>
                  {u.lockedUntil && new Date(u.lockedUntil) > new Date() && (
                    <button
                      className="btn sm"
                      title="Clear the lockout"
                      onClick={async () => {
                        await api.unlockUser(u.id);
                        await load();
                      }}
                    >
                      🔓
                    </button>
                  )}
                  <button
                    className="btn sm"
                    disabled={u.id === session?.id}
                    title={u.id === session?.id ? 'You cannot delete your own account' : 'Delete'}
                    onClick={() => void remove(u)}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  No accounts yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  help,
  width,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  help?: string;
  width?: number;
}) {
  return (
    <div style={{ flex: width ? `0 0 ${width}px` : 1, minWidth: 120 }}>
      <label>{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%' }}
      />
      {help && (
        <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
          {help}
        </div>
      )}
    </div>
  );
}

function UserForm({
  draft,
  onSaved,
  onCancel,
  onError,
}: {
  draft: Draft;
  onSaved: (msg: string) => void | Promise<void>;
  onCancel: () => void;
  onError: (m: string) => void;
}) {
  const [d, setD] = useState<Draft>(draft);
  const [password, setPassword] = useState('');
  // Loaded here rather than by the parent: only this form needs them, and only
  // when the role is CUSTOMER.
  const [customers, setCustomers] = useState<Customer[]>([]);
  useEffect(() => {
    // Surface a load failure instead of silently showing an empty picker — an
    // empty customer dropdown was otherwise indistinguishable from "no customers".
    void api.listCustomers().then(setCustomers).catch((e) => onError(`Could not load customers: ${(e as Error).message}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A CUSTOMER login's company is the customer it is linked to, not a free-text
  // field — so when one is picked, its company + address come from that record.
  const [linkedCustomer, setLinkedCustomer] = useState<CustomerDetail | null>(null);
  const isCustomer = (d.role ?? 'OPERATOR') === 'CUSTOMER';
  useEffect(() => {
    if (!isCustomer || !d.customerId) {
      setLinkedCustomer(null);
      return;
    }
    let live = true;
    void api
      .customerDetail(d.customerId)
      .then((c) => live && setLinkedCustomer(c))
      .catch(() => live && setLinkedCustomer(null));
    return () => {
      live = false;
    };
  }, [isCustomer, d.customerId]);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const isNew = !d.id;

  const set = (k: keyof Draft) => (v: string) => setD((cur) => ({ ...cur, [k]: v }));
  const val = (k: keyof Draft) => (d[k] as string) ?? '';
  // null = the inline "new customer" input is closed; a string = open with draft.
  const [newCust, setNewCust] = useState<string | null>(null);

  // Create a customer inline and select it, so a new tenant does not mean
  // leaving this form. Name only — the rest is filled in on the Customers page.
  const createCustomerInline = async (name: string) => {
    if (!name.trim()) return;
    try {
      const c = await api.createCustomer({ name: name.trim() });
      setCustomers((cs) => [...cs, c].sort((a, b) => a.name.localeCompare(b.name)));
      setD((cur) => ({ ...cur, customerId: c.id }));
      setNewCust(null);
    } catch (e) {
      onError((e as Error).message);
    }
  };

  // Suggest the login name from the email, since that is what people type at
  // the sign-in screen anyway.
  useEffect(() => {
    if (isNew && d.email && !d.name) setD((cur) => ({ ...cur, name: cur.email ?? '' }));
  }, [d.email, d.name, isNew]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: (d.name ?? '').trim(),
        role: d.role ?? 'OPERATOR',
        active: d.active ?? true,
        // The tenant a CUSTOMER login belongs to — required for that role and
        // refused without it (the picker sets it, but it was being dropped here,
        // so every CUSTOMER create failed validation). Sent for staff too, where
        // it is an optional attachment.
        customerId: d.customerId ?? null,
        email: d.email?.trim() || null,
        firstName: d.firstName ?? null,
        lastName: d.lastName ?? null,
        phoneMobile: d.phoneMobile ?? null,
        street: d.street ?? null,
        houseNumber: d.houseNumber ?? null,
        postalCode: d.postalCode ?? null,
        city: d.city ?? null,
        country: d.country ?? null,
        // A CUSTOMER's company is the linked customer, so its own free-text
        // company fields are cleared — keeping a second, drifting copy is exactly
        // what caused a tenant to show a company unrelated to its customer.
        companyName: isCustomer ? null : d.companyName ?? null,
        companyStreet: isCustomer ? null : d.companyStreet ?? null,
        companyHouseNumber: isCustomer ? null : d.companyHouseNumber ?? null,
        companyPostalCode: isCustomer ? null : d.companyPostalCode ?? null,
        companyCity: isCustomer ? null : d.companyCity ?? null,
        companyCountry: isCustomer ? null : d.companyCountry ?? null,
        companyPhone: isCustomer ? null : d.companyPhone ?? null,
        notes: d.notes ?? null,
      };

      if (isNew) {
        await api.createUser({
          ...payload,
          ...(password ? { password } : {}),
          ...(pin ? { pin } : {}),
        });
        await onSaved(`${payload.name} created.`);
      } else {
        await api.updateUser(d.id!, payload);
        // Credentials are their own endpoints: setting a password ends that
        // user's sessions, which must not be a side effect of an address edit.
        if (password) await api.setUserPassword(d.id!, password);
        if (pin) await api.setUserPin(d.id!, pin);
        await onSaved(`${payload.name} updated.`);
      }
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ marginBottom: 20 }}>
      <strong>{isNew ? 'New user' : `Editing ${displayName(d as UserProfile)}`}</strong>

      <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginTop: 16 }}>
        Person
      </h3>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <Field label="First name" value={val('firstName')} onChange={set('firstName')} />
        <Field label="Last name" value={val('lastName')} onChange={set('lastName')} />
        <Field label="Email" type="email" value={val('email')} onChange={set('email')} />
        <Field label="Mobile" value={val('phoneMobile')} onChange={set('phoneMobile')} />
      </div>

      <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginTop: 16 }}>
        Home address
      </h3>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <Field label="Street" value={val('street')} onChange={set('street')} />
        <Field label="No." value={val('houseNumber')} onChange={set('houseNumber')} width={90} />
        <Field label="Postcode" value={val('postalCode')} onChange={set('postalCode')} width={130} />
        <Field label="City" value={val('city')} onChange={set('city')} />
        <Field label="Country" value={val('country')} onChange={set('country')} />
      </div>

      <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginTop: 16 }}>
        Company
      </h3>
      {isCustomer ? (
        // The company IS the linked customer, read from its record — not a
        // separate field to keep in sync. Edited in Customers, shown here.
        <div
          style={{
            padding: 12,
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          {!d.customerId ? (
            <span className="muted">Pick a customer below — its company and address appear here.</span>
          ) : !linkedCustomer ? (
            <span className="muted">Loading the customer&rsquo;s details…</span>
          ) : (
            <>
              <div style={{ fontWeight: 600 }}>
                {linkedCustomer.legalName || linkedCustomer.name}
              </div>
              <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                {[
                  [linkedCustomer.street, linkedCustomer.houseNumber].filter(Boolean).join(' '),
                  [linkedCustomer.postalCode, linkedCustomer.city].filter(Boolean).join(' '),
                  linkedCustomer.country,
                ]
                  .filter(Boolean)
                  .join(', ') || 'No address on file yet'}
                {linkedCustomer.phone ? ` · ${linkedCustomer.phone}` : ''}
              </div>
              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                From the customer record — edit it in Customers, not here.
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Field label="Company name" value={val('companyName')} onChange={set('companyName')} />
          <Field label="Street" value={val('companyStreet')} onChange={set('companyStreet')} />
          <Field
            label="No."
            value={val('companyHouseNumber')}
            onChange={set('companyHouseNumber')}
            width={90}
          />
          <Field
            label="Postcode"
            value={val('companyPostalCode')}
            onChange={set('companyPostalCode')}
            width={130}
          />
          <Field label="City" value={val('companyCity')} onChange={set('companyCity')} />
          <Field label="Country" value={val('companyCountry')} onChange={set('companyCountry')} />
          <Field label="Phone" value={val('companyPhone')} onChange={set('companyPhone')} />
        </div>
      )}

      <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginTop: 16 }}>
        Access
      </h3>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field
          label="Login name *"
          value={val('name')}
          onChange={set('name')}
          help="What they type to sign in. Their email works too."
        />
        <div style={{ flex: '0 0 180px' }}>
          <label>Role</label>
          <select
            value={d.role ?? 'OPERATOR'}
            onChange={(e) =>
              setD((c) => ({ ...c, role: e.target.value as 'OPERATOR' | 'ADMIN' | 'CUSTOMER' }))
            }
            style={{ width: '100%' }}
          >
            <option value="OPERATOR">Operator</option>
            <option value="ADMIN">Admin</option>
            <option value="CUSTOMER">Customer (portal)</option>
          </select>
        </div>
        {/*
          The customer picker appears only for a portal login, because that is
          the only role for which it is required — and the database refuses a
          CUSTOMER row without one. Staff may have a customer too, but that is
          an attachment rather than a scope, so it is not asked for here.
        */}
        {d.role === 'CUSTOMER' && (
          <div style={{ flex: '1 1 220px' }}>
            <label>Which customer they see</label>
            <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
              <div style={{ flex: 1 }}>
                <Combobox
                  options={customers.map((c) => ({ value: String(c.id), label: c.name }))}
                  value={d.customerId ? String(d.customerId) : null}
                  onChange={(v) => setD((c) => ({ ...c, customerId: v ? Number(v) : null }))}
                  placeholder="Search customers…"
                  emptyLabel="— pick a customer —"
                  allowEmpty={false}
                />
              </div>
              {/* Create the customer here, so a new tenant is one screen rather
                  than a trip to Customers and back. */}
              {newCust === null ? (
                <button className="btn sm" type="button" onClick={() => setNewCust('')}>
                  + New
                </button>
              ) : (
                <>
                  <input
                    autoFocus
                    placeholder="Customer name"
                    value={newCust}
                    onChange={(e) => setNewCust(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); if (newCust.trim()) void createCustomerInline(newCust); }
                      if (e.key === 'Escape') setNewCust(null);
                    }}
                  />
                  <button className="btn sm primary" type="button" disabled={!newCust.trim()} onClick={() => void createCustomerInline(newCust)}>Create</button>
                  <button className="btn sm" type="button" onClick={() => setNewCust(null)}>Cancel</button>
                </>
              )}
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              This login sees only this customer&rsquo;s figures, and no staff pages at all.
            </div>
          </div>
        )}
        <Field
          label={isNew ? 'Password' : 'New password'}
          type="password"
          value={password}
          onChange={setPassword}
          help={
            isNew
              ? 'At least 10 characters. Length beats symbols.'
              : 'Leave blank to keep it. Changing it signs them out everywhere.'
          }
        />
        <Field
          label="Kiosk PIN"
          value={pin}
          onChange={setPin}
          width={120}
          help="4–6 digits, optional"
        />
        <label className="row" style={{ gap: 8, fontSize: 13, paddingBottom: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={d.active ?? true}
            onChange={(e) => setD((c) => ({ ...c, active: e.target.checked }))}
          />
          Active
        </label>
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Notes</label>
        <textarea
          rows={2}
          value={val('notes')}
          onChange={(e) => setD((c) => ({ ...c, notes: e.target.value }))}
          style={{ width: '100%' }}
        />
      </div>

      <div className="row" style={{ marginTop: 16 }}>
        <button
          className="btn primary"
          disabled={!(d.name ?? '').trim() || busy || (isNew && !password && !pin)}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : isNew ? 'Create user' : 'Save changes'}
        </button>
        <button className="btn" onClick={onCancel}>
          Cancel
        </button>
        {isNew && !password && !pin && (
          <span className="muted" style={{ fontSize: 12 }}>
            Give them a password or a PIN — an account with neither cannot sign in.
          </span>
        )}
      </div>
    </div>
  );
}
